import { useState } from 'react'
import { FiPlus, FiTrash2 } from 'react-icons/fi'
import {
  StockDispatchDetailApi,
  deleteDetail,
  formatDate,
  getTruckRegistrationEntries,
} from '@/services/api/stockDispatch'

const thStyle: React.CSSProperties = {
  fontFamily: 'Lexend Deca',
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '20px',
  color: '#FFFFFF',
  paddingLeft: '12px',
  paddingRight: '12px',
  borderBottom: '1px solid #E9E9EA',
  whiteSpace: 'nowrap',
}

const tdStyle: React.CSSProperties = {
  fontFamily: 'Lexend Deca',
  fontWeight: 400,
  fontSize: '13px',
  lineHeight: '22px',
  color: '#212126',
  paddingLeft: '12px',
  paddingRight: '12px',
  whiteSpace: 'nowrap',
}

interface TruckRegistrationTableProps {
  stockDispatchId: string
  details?: StockDispatchDetailApi[]
  onAdd: () => void
  onChanged: () => void
  compact?: boolean
}

const TruckRegistrationTable = ({
  stockDispatchId,
  details,
  onAdd,
  onChanged,
  compact = false,
}: TruckRegistrationTableProps) => {
  const rows = getTruckRegistrationEntries(details)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const handleDelete = async (detailId: string, truckReg: string) => {
    if (!window.confirm(`Remove truck registration "${truckReg}"?`)) return
    try {
      setDeletingId(detailId)
      await deleteDetail(stockDispatchId, detailId)
      onChanged()
    } catch {
      // Global API toast handles errors
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div style={{ padding: compact ? '12px 16px' : '16px 20px', backgroundColor: '#F8F9FB' }}>
      <div className="flex items-center justify-between" style={{ marginBottom: '12px' }}>
        <h4
          style={{
            fontFamily: 'Lexend Deca',
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '22px',
            color: '#212126',
          }}
        >
          Truck Registration
        </h4>
        <button
          type="button"
          onClick={onAdd}
          className="flex items-center gap-1 hover:opacity-90 transition-opacity"
          style={{
            fontFamily: 'Lexend Deca',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '20px',
            color: '#212126',
            backgroundColor: '#FFFFFF',
            border: '1px solid #E9E9EA',
            borderRadius: '6px',
            padding: '6px 10px',
            height: '32px',
          }}
        >
          <FiPlus className="w-3.5 h-3.5" />
          Add Truck
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: '520px' }}>
          <thead>
            <tr style={{ backgroundColor: '#39553A' }}>
              <th className="text-left py-2 text-xs" style={{ ...thStyle, borderTopLeftRadius: '8px' }}>
                #
              </th>
              <th className="text-left py-2 text-xs" style={thStyle}>
                Truck Registration
              </th>
              <th className="text-left py-2 text-xs" style={thStyle}>
                Transporter
              </th>
              <th className="text-left py-2 text-xs" style={thStyle}>
                Truck Rate
              </th>
              <th className="text-left py-2 text-xs" style={thStyle}>
                Dispatched Date
              </th>
              <th
                className="text-left py-2 text-xs"
                style={{ ...thStyle, borderTopRightRadius: '8px' }}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr style={{ backgroundColor: '#FFFFFF' }}>
                <td
                  colSpan={6}
                  className="py-4 text-center text-xs"
                  style={{ fontFamily: 'Lexend Deca', color: '#69697A', borderBottom: '1px solid #E9E9EA' }}
                >
                  No truck registrations yet. Click Add Truck to register a vehicle.
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr
                  key={row.id}
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderBottom: index === rows.length - 1 ? 'none' : '1px solid #E9E9EA',
                  }}
                >
                  <td className="py-2 text-xs" style={tdStyle}>
                    {index + 1}
                  </td>
                  <td className="py-2 text-xs" style={{ ...tdStyle, fontWeight: 500 }}>
                    {row.truckReg}
                  </td>
                  <td className="py-2 text-xs" style={tdStyle}>
                    {row.transporter || '—'}
                  </td>
                  <td className="py-2 text-xs" style={tdStyle}>
                    {row.truckRate ?? '—'}
                  </td>
                  <td className="py-2 text-xs" style={tdStyle}>
                    {formatDate(row.dispatchedDate)}
                  </td>
                  <td className="py-2 text-xs" style={tdStyle}>
                    <button
                      type="button"
                      onClick={() => handleDelete(row.id, row.truckReg || '')}
                      disabled={deletingId === row.id}
                      className="hover:opacity-80 disabled:opacity-50"
                      title="Remove truck registration"
                      style={{ color: '#E93323' }}
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default TruckRegistrationTable
